import React, { Fragment, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Stack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  HStack,
  Center,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Grid,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { FaTrashAlt, FaPencilAlt, FaEye } from 'react-icons/fa';
import { useHistory } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import axios from 'axios';

import AuthContext from '../contexts/AuthContext';
import { useMultiState } from '../lib/hooks';

import getAnnouncesQuery from '../graphql/queries/getAnnounces';
import createAnnounceMutation from '../graphql/mutations/createAnnounce';
import updateAnnounceByIdMutation from '../graphql/mutations/updateAnnounceById';
import removeAnnounceByIdMutation from '../graphql/mutations/removeAnnounceById';
// add i18n
import { useTranslation } from 'react-i18next';
import { GrAnnounce } from 'react-icons/gr';
import { LinkifyText } from '../lib/LinkifyText';

const URL = process.env.REACT_APP_UPLOAD_IMAGE;

const toLocalDateTimeInput = (iso) => {
  try {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => `${n}`.padStart(2, '0');
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
  } catch {
    return '';
  }
};

const toISO = (value) => (value ? new Date(value).toISOString() : null);

const initialAnnounce = {
  _id: '',
  title: '',
  content: '',
  startDate: '',
  endDate: '',
  image: '',
};

const MainWebController = () => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const { t } = useTranslation();

  const { data, refetch, loading } = useQuery(getAnnouncesQuery);
  const [createAnnounce] = useMutation(createAnnounceMutation);
  const [updateAnnounce] = useMutation(updateAnnounceByIdMutation);
  const [removeAnnounce] = useMutation(removeAnnounceByIdMutation);

  const [announceState, setAnnounceState] = useState([]);
  const [newphotoData, setnewphotoData] = useMultiState({ data: null, base64: '' });

  const { isOpen: addOpen, onOpen: addModalOpen, onClose: addModalClose } = useDisclosure();
  const { isOpen: editOpen, onOpen: editModalOpen, onClose: editModalClose } = useDisclosure();
  const { isOpen: removeOpen, onOpen: removeModalOpen, onClose: removeModalClose } = useDisclosure();
  const { isOpen: viewOpen, onOpen: viewModalOpen, onClose: viewModalClose } = useDisclosure();

  const [announceAddData, setAnnounceAddData] = useMultiState(initialAnnounce);
  const [announceEditData, setAnnounceEditData] = useMultiState(initialAnnounce);
  const [announceRemoveData, setAnnounceRemoveData] = useMultiState(initialAnnounce);
  const [announceViewData, setAnnounceViewData] = useMultiState(initialAnnounce);

  useEffect(() => {
    if (data?.announces) setAnnounceState(data.announces);
    if (user?.role !== 'ADMIN') {
      setTimeout(() => history.push('/login'), 100);
    }
    if (loading) refetch();
  }, [data, history, user, loading, refetch]);

  const selectPhotoFile = (e) => {
    new Promise((resolve, reject) => {
      e.preventDefault();
      const file = e.currentTarget.files?.[0];
      if (!file) return;
      setnewphotoData({ data: file });
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
        setnewphotoData({ base64: reader.result });
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const resetImage = () => setnewphotoData({ data: null, base64: '' });

  const uploadImageIfAny = async () => {
    if (!newphotoData?.base64) return null;
    const res = await axios.post(URL, { photo: newphotoData.base64 });
    return res?.data?.url || null;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!announceAddData.title || !announceAddData.content || !announceAddData.startDate || !announceAddData.endDate) {
      await toast({
        position: 'top',
        title: t('mainWebController.toast.successTitle'), // keep a title; or change to a warning key if you have
        description: t('mainWebController.toast.required'),
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    try {
      let imageUrl = null;
      if (newphotoData?.base64) {
        const response = await axios.post(URL, { photo: newphotoData.base64 });
        imageUrl = response?.data?.url || null;
      }
      const { data: created } = await createAnnounce({
        variables: {
          title: announceAddData.title,
          content: announceAddData.content,
          startDate: toISO(announceAddData.startDate),
          endDate: toISO(announceAddData.endDate),
          image: imageUrl,
        },
      });
      if (created) {
        await toast({
          position: 'top',
          title: t('mainWebController.toast.successTitle'),
          description: t('mainWebController.toast.created'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setAnnounceAddData(initialAnnounce);
        resetImage();
        addModalClose();
        refetch();
      }
    } catch (err) {
      await toast({
        position: 'top',
        title: t('mainWebController.toast.errorTitle'),
        description: `${err}`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!announceEditData._id) return;
    try {
      let imageUrl = announceEditData.image || null;
      if (newphotoData?.base64) {
        const response = await axios.post(URL, { photo: newphotoData.base64 });
        imageUrl = response?.data?.url || null;
      }
      const { data: updated } = await updateAnnounce({
        variables: {
          id: announceEditData._id,
          title: announceEditData.title,
          content: announceEditData.content,
          startDate: announceEditData.startDate ? toISO(announceEditData.startDate) : undefined,
          endDate: announceEditData.endDate ? toISO(announceEditData.endDate) : undefined,
          image: imageUrl,
        },
      });
      if (updated) {
        await toast({
          position: 'top',
          title: t('mainWebController.toast.successTitle'),
          description: t('mainWebController.toast.updated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setAnnounceEditData(initialAnnounce);
        resetImage();
        editModalClose();
        refetch();
      }
    } catch (err) {
      await toast({
        position: 'top',
        title: t('mainWebController.toast.errorTitle'),
        description: `${err}`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleRemove = async (e) => {
    e.preventDefault();
    if (!announceRemoveData._id) return;
    try {
      const { data: removed } = await removeAnnounce({ variables: { id: announceRemoveData._id } });
      if (removed) {
        await toast({
          position: 'top',
          title: t('mainWebController.toast.successTitle'),
          description: t('mainWebController.toast.removed'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setAnnounceRemoveData(initialAnnounce);
        removeModalClose();
        refetch();
      }
    } catch (err) {
      await toast({
        position: 'top',
        title: t('mainWebController.toast.errorTitle'),
        description: `${err}`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  return (
    <Fragment>
      <Modal
        isOpen={addOpen}
        onClose={() => {
          addModalClose();
          resetImage();
        }}
        size='xl'
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('mainWebController.addAnnounce')}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleAdd}>
            <ModalBody>
              <FormControl>
                <FormLabel>{t('mainWebController.fields.title')}</FormLabel>
                <Input value={announceAddData.title} onChange={(e) => setAnnounceAddData({ title: e.target.value })} />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{t('mainWebController.fields.content')}</FormLabel>
                <Textarea
                  value={announceAddData.content}
                  onChange={(e) => setAnnounceAddData({ content: e.target.value })}
                />
              </FormControl>
              <HStack mt={3}>
                <FormControl>
                  <FormLabel>{t('mainWebController.fields.start')}</FormLabel>
                  <Input
                    type='datetime-local'
                    value={announceAddData.startDate}
                    onChange={(e) => setAnnounceAddData({ startDate: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('mainWebController.fields.end')}</FormLabel>
                  <Input
                    type='datetime-local'
                    value={announceAddData.endDate}
                    onChange={(e) => setAnnounceAddData({ endDate: e.target.value })}
                  />
                </FormControl>
              </HStack>
              <HStack mt={3}>
                <Button size='sm' as='label'>
                  {t('mainWebController.fields.selectImage')}
                  <input onChange={selectPhotoFile} accept='image/*' type='file' style={{ display: 'none' }} />
                </Button>
                <Text>{newphotoData?.data?.name}</Text>
              </HStack>
            </ModalBody>
            <ModalFooter>
              <Button
                mr={3}
                onClick={() => {
                  addModalClose();
                  resetImage();
                }}
              >
                {t('Button.close')}
              </Button>
              <Button
                bg='primary.700'
                color='white'
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                type='submit'
              >
                {t('Button.save')}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Edit Announce */}
      <Modal
        isOpen={editOpen}
        onClose={() => {
          editModalClose();
          resetImage();
        }}
        size='xl'
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('mainWebController.editAnnounce')}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleEdit}>
            <ModalBody>
              <FormControl>
                <FormLabel>{t('mainWebController.fields.title')}</FormLabel>
                <Input
                  value={announceEditData.title}
                  onChange={(e) => setAnnounceEditData({ title: e.target.value })}
                />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel>{t('mainWebController.fields.content')}</FormLabel>
                <Textarea
                  value={announceEditData.content}
                  onChange={(e) => setAnnounceEditData({ content: e.target.value })}
                />
              </FormControl>
              <HStack mt={3}>
                <FormControl>
                  <FormLabel>{t('mainWebController.fields.start')}</FormLabel>
                  <Input
                    type='datetime-local'
                    value={toLocalDateTimeInput(announceEditData.startDate)}
                    onChange={(e) => setAnnounceEditData({ startDate: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('mainWebController.fields.end')}</FormLabel>
                  <Input
                    type='datetime-local'
                    value={toLocalDateTimeInput(announceEditData.endDate)}
                    onChange={(e) => setAnnounceEditData({ endDate: e.target.value })}
                  />
                </FormControl>
              </HStack>
              <HStack mt={3}>
                <Button size='sm' as='label'>
                  {t('mainWebController.fields.changeImage')}
                  <input onChange={selectPhotoFile} accept='image/*' type='file' style={{ display: 'none' }} />
                </Button>
                <Text>{newphotoData?.data?.name || ''}</Text>
              </HStack>
            </ModalBody>
            <ModalFooter>
              <Button
                mr={3}
                onClick={() => {
                  editModalClose();
                  resetImage();
                }}
              >
                {t('Button.close')}
              </Button>
              <Button
                bg='primary.700'
                color='white'
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                type='submit'
              >
                {t('Button.save')}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Remove Announce */}
      <AlertDialog isOpen={removeOpen} onClose={removeModalClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <form onSubmit={handleRemove}>
              <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                {t('mainWebController.deleteAnnounce')}
              </AlertDialogHeader>
              <AlertDialogBody>{t('mainWebController.confirmDelete')}</AlertDialogBody>
              <AlertDialogFooter>
                <Button onClick={removeModalClose}>{t('Button.close')}</Button>
                <Button
                  bg='primary.700'
                  color='white'
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                  type='submit'
                  ml={3}
                >
                  {t('mainWebController.tooltip.remove')}
                </Button>
              </AlertDialogFooter>
            </form>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Box mt={20} w='100%' minHeight='90vh'>
        <Container maxW='7xl' py={5} as={Stack} spacing={12}>
          <Stack spacing={0} align='center'>
            <Heading variant='h4'>{t('mainWebController.title')}</Heading>
            <Text>{t('mainWebController.subtitle')}</Text>
          </Stack>

          <Stack align={{ base: 'center', md: 'flex-start' }}>
            <Button
              bg='primary.700'
              color='white'
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              leftIcon={<AddIcon />}
              size='lg'
              mb={3}
              onClick={() => {
                setAnnounceAddData(initialAnnounce);
                resetImage();
                addModalOpen();
              }}
            >
              {t('mainWebController.addAnnounce')}
            </Button>
          </Stack>

          <Box sx={{ overflowX: ['auto', 'auto', 'none'], maxW: ['350px', 'none'] }} borderWidth='1px'>
            <Table variant='striped' backgroundColor='white'>
              <Thead height='50px'>
                <Tr>
                  <Th textAlign='center'>{t('mainWebController.table.no')}</Th>
                  <Th textAlign='center'>{t('mainWebController.table.image')}</Th>
                  <Th textAlign='center'>{t('mainWebController.table.title')}</Th>
                  <Th textAlign='center'>{t('mainWebController.table.period')}</Th>
                  <Th textAlign='center'>{t('mainWebController.table.manage')}</Th>
                </Tr>
              </Thead>
              {announceState?.map((row, i) => (
                <Tbody key={row?._id} bg='gray.400'>
                  <Tr>
                    <Td textAlign='center' w='5vh'>
                      {i + 1}
                    </Td>
                    <Td minWidth='15vh' textAlign='center'>
                      {row?.image && (
                        <Image borderRadius='5px' boxSize='40px' objectFit='cover' src={row.image} alt='announce' />
                      )}
                    </Td>
                    <Td textAlign='center' maxWidth='40vh'>
                      {row?.title}
                    </Td>
                    <Td textAlign='center' maxWidth='40vh'>
                      <Stack spacing={0}>
                        <Text fontSize='sm'>
                          {t('mainWebController.period.start')}:{' '}
                          {toLocalDateTimeInput(row?.startDate).replace('T', ' ')}
                        </Text>
                        <Text fontSize='sm'>
                          {t('mainWebController.period.end')}: {toLocalDateTimeInput(row?.endDate).replace('T', ' ')}
                        </Text>
                      </Stack>
                    </Td>
                    <Td textAlign='center'>
                      <Stack direction='row' justify='center'>
                        <Tooltip label={t('mainWebController.tooltip.view')} fontSize='md'>
                          <IconButton
                            boxShadow='lg'
                            colorScheme='teal'
                            icon={<FaEye />}
                            onClick={(e) => {
                              e.preventDefault();
                              setAnnounceViewData({
                                _id: row?._id,
                                title: row?.title || '',
                                content: row?.content || '',
                                startDate: row?.startDate || '',
                                endDate: row?.endDate || '',
                                image: row?.image || '',
                              });
                              viewModalOpen();
                            }}
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                          />
                        </Tooltip>
                        <Tooltip label={t('mainWebController.tooltip.edit')} fontSize='md'>
                          <IconButton
                            boxShadow='lg'
                            colorScheme='blue'
                            icon={<FaPencilAlt />}
                            onClick={(e) => {
                              e.preventDefault();
                              setAnnounceEditData({
                                _id: row?._id,
                                title: row?.title || '',
                                content: row?.content || '',
                                startDate: row?.startDate || '',
                                endDate: row?.endDate || '',
                                image: row?.image || '',
                              });
                              resetImage();
                              editModalOpen();
                            }}
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                          />
                        </Tooltip>
                        <Tooltip label={t('mainWebController.tooltip.remove')} fontSize='md'>
                          <IconButton
                            boxShadow='lg'
                            colorScheme='red'
                            icon={<FaTrashAlt />}
                            onClick={(e) => {
                              e.preventDefault();
                              setAnnounceRemoveData({ _id: row?._id });
                              removeModalOpen();
                            }}
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                          />
                        </Tooltip>
                      </Stack>
                    </Td>
                  </Tr>
                </Tbody>
              ))}
            </Table>
          </Box>
        </Container>
      </Box>

      {/* View Announce modal: only header text */}
      <Modal isOpen={viewOpen} onClose={viewModalClose} size='xl'>
        <ModalOverlay />
        <ModalContent maxW='4xl' h='600px'>
          <ModalCloseButton />
          <ModalBody p={0}>
            <Grid templateColumns='1fr 1fr' h='full'>
              {/* ซ้าย - รูปภาพ */}
              <Box>
                {announceViewData?.image ? (
                  <Image
                    w='100%'
                    h='100%'
                    objectFit='cover'
                    src={announceViewData.image}
                    alt='announce'
                    borderLeftRadius='md'
                  />
                ) : (
                  <Center w='100%' h='100%' bg='gray.200' borderLeftRadius='md'>
                    <GrAnnounce size='80px' />
                  </Center>
                )}
              </Box>

              {/* ขวา - ข้อความ */}
              <Box p={6} overflowY='auto'>
                <Text fontSize='2xl' fontWeight='bold' mb={4}>
                  {announceViewData?.title || t('announcePopup.title')}
                </Text>
                <Text whiteSpace='pre-wrap' fontSize='md'>
                  {LinkifyText({ text: announceViewData?.content || '' }) || ''}
                </Text>
              </Box>
            </Grid>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Fragment>
  );
};

export default MainWebController;
